'use client'

import { FPS } from 'creation/src/creation-tools/constants'
import CreationToolCutOutMode from 'creation/src/creation-tools/types/cut-out-mode'
import getCurrentFrameCanvas from 'creation/src/creation-tools/utils/get-current-frame-canvas'
import { MouseEvent as ReactMouseEvent, useMemo, useState } from 'react'
import { overlayBlock } from '../../../presentation/blocks'
import Canvas from '../../../presentation/canvas'
import { useCreationStore } from '../../../store/provider'
import { useCutOut, type Point } from '../cut-out-context'
import { drawPoint, getDifferenceBetweenTwoPoints, lerp, pointFromEvent, POINT_SIZE_SCALE } from '../utils'
import { cn } from '@/util/cn'

type Props = {
    scale: number
}

const cursorStyle = (size: number) => ({
    cursor: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="128" height="128"><circle cx="50" cy="50" r="${size}" stroke="white" stroke-width="1" fill="transparent" /></svg>') 50 50, pointer`,
})

const Pen = ({ scale }: Props) => {
    const sources = useCreationStore((state) => state.sources)
    const { updatePaths, eraseSize, brushSize, paths, mode, editCanvas, sourceImageUrl, setBrushEnabled } = useCutOut()
    const [isPenEnabled, setIsPenEnabled] = useState<boolean>(false)

    const currentPenSize = useMemo<number>(() => {
        let size = 0

        switch (mode) {
            case CreationToolCutOutMode.Erase:
                size = eraseSize
                break
            case CreationToolCutOutMode.Paint:
                size = brushSize
                break
        }

        size *= POINT_SIZE_SCALE

        return size
    }, [mode, eraseSize, brushSize])

    // Draw canvas when we are on Paint mode to have a reference for the user
    const drawCanvas = (canvas: HTMLCanvasElement, context: CanvasRenderingContext2D, drawCount: number) => {
        if (!sources.length) return

        const { frames = [], width, height } = sources[0]
        const frame = getCurrentFrameCanvas(frames, drawCount)
        canvas.width = width
        canvas.height = height
        frame && context.drawImage(frame, 0, 0)
    }

    const updatePenPosition = (e: ReactMouseEvent<HTMLDivElement>, forcePenEnabled?: boolean) => {
        const drawFrame = getCurrentFrameCanvas(editCanvas)

        if ((isPenEnabled || forcePenEnabled) && drawFrame && sourceImageUrl) {
            const { x, y } = pointFromEvent(e.nativeEvent, e.target as Element, scale)
            const path = paths[paths?.length - 1]
            const points = paths[paths?.length - 1]?.points || []
            const nextPoint: Point = {
                x,
                y,
            }

            if (points.length === 0) {
                drawFrame && drawPoint(drawFrame, path, nextPoint, sourceImageUrl, scale)
                paths[paths?.length - 1]?.points?.push(nextPoint)
            } else if (points.length > 0) {
                const lastPoint = points[points.length - 1]
                let nextFillingPoint = nextPoint
                let distance = 0.1

                while (
                    (getDifferenceBetweenTwoPoints(lastPoint, nextPoint).x > 5 ||
                        getDifferenceBetweenTwoPoints(lastPoint, nextPoint).y > 5) &&
                    distance <= 1
                ) {
                    nextFillingPoint = lerp(nextFillingPoint, lastPoint, distance)
                    distance += 0.05
                    drawFrame && drawPoint(drawFrame, path, nextFillingPoint, sourceImageUrl, scale)
                }

                paths[paths?.length - 1]?.points?.push(nextPoint)
                drawFrame && drawPoint(drawFrame, path, nextPoint, sourceImageUrl, scale)
            }

            updatePaths(paths)
        }
    }

    const handleStartPen = (e: ReactMouseEvent<HTMLDivElement>) => {
        paths[0] = {
            mode,
            size: currentPenSize,
            points: [],
        }
        setBrushEnabled()
        setIsPenEnabled(true)
        updatePenPosition(e, true)
    }

    return (
        <div className={cn(overlayBlock, 'select-none', mode === CreationToolCutOutMode.Paint && 'opacity-60')}>
            <div
                data-testid="pen-board"
                className="h-full w-full"
                onMouseDown={handleStartPen}
                onMouseUp={() => setIsPenEnabled(false)}
                onMouseMove={updatePenPosition}
                style={cursorStyle(currentPenSize)}
            />
            {mode === CreationToolCutOutMode.Paint && (
                <div
                    data-testid="paint-reference"
                    className={cn(overlayBlock, 'pointer-events-none opacity-50 [&_canvas]:w-full')}
                >
                    <Canvas draw={drawCanvas} fps={FPS} />
                </div>
            )}
        </div>
    )
}

export default Pen
